import { AppState } from '../../../redux/reducers';
import { connect } from 'react-redux';
import {
  FontIcon,
  IBasePickerSuggestionsProps,
  ITag,
  MessageBarType,
  SearchBox,
  Spinner,
  TagPicker
} from '@fluentui/react';

// Store
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware';
import * as PractitionerSpecialtyAssociationStore from '../../../redux/middleware/PractitionerSpecialtyAssociationMiddleware';
import * as SpecialtiesStore from '../../../redux/middleware/Specialties.Middleware';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { ErrorType } from '../../../models/HttpError';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultButton } from '@fluentui/react';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PractitionerAssociationExportFormComponent from '../../Protocols/Practitioners/PractitionerAssociationExportForm/PractitionerAssociationExport.Form.Component';
import { SpecialtyViewModel } from '../../../models/Entities/Specialties/SpecialtyModels';

////i18Next
import { useTranslation } from 'react-i18next';

// styles
import './PractitionerList.style.css';

interface IDispatchProps {
  GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
  GetSpecialtiesByPractitionerId: typeof PractitionerSpecialtyAssociationStore.actionCreators.GetSpecialtiesByPractitionerId;
  GetAllPractitionerSpecialtyAssociations: typeof PractitionerSpecialtyAssociationStore.actionCreators.GetAllPractitionerSpecialtyAssociations;
  GetAllSpecialties: typeof SpecialtiesStore.actionCreators.GetAllSpecialties;
}

interface IConnectedProps {
  practitioners: Practitioner[];
  isLoading: boolean;
  isLoadingSuccess: boolean;
  failOnLoading: boolean;
  error: ErrorType;
  specialties: SpecialtyViewModel[];
}

type IProps = IDispatchProps & IConnectedProps;

export const PractitionerListComponent = ({
  practitioners,
  isLoading,
  isLoadingSuccess,
  failOnLoading,
  error,
  specialties,
  GetAllPractitioners,
  GetSpecialtiesByPractitionerId,
  GetAllPractitionerSpecialtyAssociations,
  GetAllSpecialties
}: IProps) => {

  const [searchText, setSearchText] = useState('');
  const [selectedSpecialtyTags, setSelectedSpecialtyTags] = useState<ITag[]>([]);
  const [isExportFormOpen, setIsExportFormOpen] = useState<boolean>(false);
  const specialtyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const specialtyById = useMemo(() => {
    return new Map<number, SpecialtyViewModel>((specialties ?? []).map((s) => [s.id, s]));
  }, [specialties]);

  const getSpecialtyName = useCallback(
    (specialtyId: number, fallbackName?: string | null): string | undefined => {
      return fallbackName ?? specialtyById.get(specialtyId)?.name ?? undefined;
    },
    [specialtyById]
  );

  const getPractitionerSpecialties = useCallback((practitioner: Practitioner): any[] => {
    const direct = (practitioner as any)?.SpecialtiesAssociated;
    if (Array.isArray(direct)) return direct;

    const camel = (practitioner as any)?.specialtiesAssociated;
    if (Array.isArray(camel)) return camel;

    return [];
  }, []);

  const filterSpecialtySuggestions = useCallback(
    (filterText: string, tagList?: ITag[]): Promise<ITag[]> => {
      if (filterText.trim().length < 3) return Promise.resolve([]);
      if (specialtyDebounceRef.current) clearTimeout(specialtyDebounceRef.current);
      return new Promise<ITag[]>((resolve) => {
        specialtyDebounceRef.current = setTimeout(() => {
          const existingKeys = new Set(tagList?.map((t) => t.key) ?? []);
          const seen = new Set<number>();
          const suggestions: ITag[] = [];
          practitioners?.forEach((p) => {
            getPractitionerSpecialties(p).forEach((sa) => {
              const specialtyName = getSpecialtyName(sa.specialtyId, sa.specialty?.name ?? undefined);
              const specialtyId = Number(sa.specialtyId);
              if (
                specialtyName &&
                !Number.isNaN(specialtyId) &&
                !seen.has(specialtyId) &&
                !existingKeys.has(specialtyId) &&
                specialtyName.toLowerCase().includes(filterText.trim().toLowerCase())
              ) {
                seen.add(specialtyId);
                suggestions.push({ key: specialtyId, name: specialtyName });
              }
            });
          });
          resolve(suggestions);
        }, 300);
      });
    },
    [practitioners, getSpecialtyName, getPractitionerSpecialties]
  );

  const navigate = useNavigate();
  const [t] = useTranslation();

  const onViewPractitionerEvent = (practitioner: Practitioner) => {
    navigate('/practitioners/' + practitioner.userId);
  };

  const onSearch = (newValue: string) => {
    setSearchText(newValue);
  };

  useEffect(() => {
    GetAllPractitioners(1, 1000, '');
  }, []);

  // Tres cargas independientes: practitioners (arriba), todas las asociaciones médico / especialidad y el catálogo
  // de especialidades. El reductor de practitioners las cruza en el orden en que lleguen.
  useEffect(() => {
    if (process.env.REACT_APP_PREVIEW_FEATURES !== 'true') return;
    GetAllPractitionerSpecialtyAssociations();
    if (!specialties || specialties.length === 0) {
      GetAllSpecialties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPractitioners = practitioners?.filter((item) =>
    (item.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.registrationNumber?.toString().includes(searchText)) &&
    (selectedSpecialtyTags.length === 0 ||
      selectedSpecialtyTags.every((tag) =>
        getPractitionerSpecialties(item).some((sa) => Number(sa.specialtyId) === Number(tag.key))
      ))
  ) ?? [];

  return (
    <>
      {/* ── Header controls ── */}
      <div className="prac-list__header">
        <SearchBox
          className="prac-list__search"
          placeholder={t('practitionerList.parameters.search')}
          underlined={false}
          onChange={(_, newValue) => onSearch(newValue || '')}
          onClear={() => onSearch('')}
        />
        {process.env.REACT_APP_PREVIEW_FEATURES === 'true' && (
          <div className="prac-list__specialty-wrap">
            <TagPicker
              selectedItems={selectedSpecialtyTags}
              onResolveSuggestions={filterSpecialtySuggestions}
              getTextFromItem={(item: ITag) => item.name}
              pickerSuggestionsProps={{
                suggestionsHeaderText: t('practitionerList.parameters.specialtySuggestionsHeader'),
                noResultsFoundText: t('practitionerList.parameters.specialtyNoResults')
              } as IBasePickerSuggestionsProps}
              onChange={(items?: ITag[]) => setSelectedSpecialtyTags(items ?? [])}
              inputProps={{
                placeholder: t('practitionerList.parameters.searchSpecialty'),
                autoComplete: 'off'
              }}
            />
          </div>
        )}
        <DefaultButton
          iconProps={{ iconName: 'ExcelDocument' }}
          title={t('practitionerAssociationExport.title')}
          ariaLabel={t('practitionerAssociationExport.title')}
          onClick={() => setIsExportFormOpen(true)}
        >
          Exportar visitas
        </DefaultButton>
      </div>

      <PractitionerAssociationExportFormComponent
        isOpen={isExportFormOpen}
        onClose={() => setIsExportFormOpen(false)}
      />

      {failOnLoading && (
        <MessageComponent
          message={t('practitionerList.parameters.errorMessage')}
          subMessage={error.ErrorMessage}
          type={MessageBarType.error}
          itemList={error.Errors}
        />
      )}

      {(searchText || selectedSpecialtyTags.length > 0) && (
        <MessageComponent
          message={
            t('practitionerList.parameters.filterPrefix') +
            [
              searchText ? `${t('practitionerList.parameters.filterDoctor')}: "${searchText}"` : '',
              selectedSpecialtyTags.length > 0
                ? `${t('practitionerList.parameters.filterSpecialties')}: ${selectedSpecialtyTags.map((tag) => tag.name).join(', ')}`
                : ''
            ]
              .filter(Boolean)
              .join(' - ')
          }
          type={MessageBarType.info}
        />
      )}

      {isLoading && (
        <div className="prac-list__loading">
          <Spinner
            label={t('practitionerList.loadingSucces.medicalProfessionals')}
            ariaLive="assertive"
            labelPosition="right"
          />
        </div>
      )}

      {isLoadingSuccess && filteredPractitioners.length === 0 && (
        <div className="prac-list__empty">
          <FontIcon iconName="UserOptional" className="prac-list__empty-icon" />
          <p>{t('practitionerList.loadingSucces.medicalProfessionals')}</p>
        </div>
      )}

      {isLoadingSuccess && filteredPractitioners.length > 0 && (
        <div className="prac-list__grid">
          {filteredPractitioners.map((item) => {
            const cardClass = item.active ? 'prac-card' : 'prac-card prac-card--inactive';
            return (
            <div key={item.userId} className="prac-card-wrapper">
              <button
                className={cardClass}
                onClick={() => onViewPractitionerEvent(item)}
              >
                <div className="prac-card__accent" />
                <div className="prac-card__body">

                  <div className="prac-card__header">
                    <div className="prac-card__name-group">
                      <FontIcon iconName="UserOptional" className="prac-card__user-icon" />
                      <span className="prac-card__name">{item.fullName.toUpperCase()}</span>
                    </div>
                    <span className={`prac-card__badge${item.active ? ' prac-card__badge--active' : ' prac-card__badge--inactive'}`}>
                      {item.active
                        ? t('practitionerList.loadingSucces.enable')
                        : t('practitionerList.loadingSucces.disabled')}
                    </span>
                  </div>

                  <div className="prac-card__registration">
                    <span className="prac-card__reg-label">{t('practitionerList.loadingSucces.registration')}:</span>
                    <strong>{item.registrationNumber}</strong>
                    {item.speciality && (
                      <span className="prac-card__specialty"> · {item.speciality}</span>
                    )}
                  </div>

                  {process.env.REACT_APP_PREVIEW_FEATURES === 'true' &&
                    getPractitionerSpecialties(item).length > 0 && (
                      <div className="prac-card__tags">
                        {getPractitionerSpecialties(item).map((sa) => {
                          const specialtyName = getSpecialtyName(sa.specialtyId, sa.specialty?.name ?? undefined);
                          if (!specialtyName) return null;
                          return <span key={sa.id} className="prac-card__tag">{specialtyName}</span>;
                        })}
                      </div>
                    )}

                  <div className="prac-card__footer">
                    <FontIcon iconName="NavigateForward" className="prac-card__nav-icon" />
                    <span>{t('practitionerList.loadingSucces.information')}</span>
                  </div>

                </div>
              </button>
            </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    practitioners: state.Practitioners?.list,
    isLoading: state.Practitioners?.isLoadingAll,
    isLoadingSuccess: state.Practitioners?.successLoadingAll,
    failOnLoading: state.Practitioners?.failOnLoadingAll,
    error: state.Practitioners?.error,
    specialties: state.Specialties?.list ?? []
  };
};

const mapDispatchToProps = {
  ...PractitionerStore.actionCreators,
  ...PractitionerSpecialtyAssociationStore.actionCreators,
  ...SpecialtiesStore.actionCreators
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PractitionerListComponent as any);
